// SettingsLayout.jsx 
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Divider,
  Stack,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Sidebar from '../landingPage/Sidebar';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  getPublicTimezoneSettings,
  updatePublicTimezoneSettings,
  getPublicTimezoneOptions,
  getPublicDateFormatOptions,
  getCurrentUserProfile,
  updateCurrentUserProfile,
} from '../../apis/Admin';
import { changePassword } from '../../apis/ChangePwd';
import Cookies from 'js-cookie';

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    height: 45,
    alignItems: 'center',
    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#5965A6',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#5965A6',
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: '#d32f2f',
  },
}));

export default function SettingsLayout() {
  const [profile, setProfile] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [profileErrors, setProfileErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [timezoneSettings, setTimezoneSettings] = useState({
    defaultTimezone: '',
    secondaryTimezone: '',
    dateFormat: '',
    alsoShowTimesInUtc: false,
  });
  
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [dateFormatOptions, setDateFormatOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [originalProfile, setOriginalProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const handleProfileChange = (field) => (e) => {
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleTimezoneChange = (field) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value;
    setTimezoneSettings((prev) => ({ ...prev, [field]: value }));
  };


  const toggleShowPassword = (field) => () => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePasswordFields = () => {
    const { currentPassword, newPassword, confirmPassword } = profile;
    const errors = {};

    const currentPw = currentPassword?.trim() || '';
    const newPw = newPassword?.trim() || '';
    const confirmPw = confirmPassword?.trim() || '';

    const hasAllPasswordFields = currentPw && newPw && confirmPw;
    const hasAnyPasswordField = currentPw || newPw || confirmPw;

    if (!hasAnyPasswordField) {
      setProfileErrors({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      return true;
    }

    if (!hasAllPasswordFields) {
      setProfileErrors({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      return true; 
    }

    if (!currentPw) {
      errors.currentPassword = 'Current password is required';
    }

    if (!newPw) {
      errors.newPassword = 'New password is required';
    } else if (newPw.length < 8) {
      errors.newPassword = 'Minimum 8 characters';
    }

    if (!confirmPw) {
      errors.confirmPassword = 'Confirm your new password';
    } else if (confirmPw !== newPw) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setProfileErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = async () => {
    const currentPw = profile.currentPassword?.trim() || '';
    const newPw = profile.newPassword?.trim() || '';
    const confirmPw = profile.confirmPassword?.trim() || '';
    
    const hasAllPasswordFields = currentPw && newPw && confirmPw;
    
    setProfileErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

    if (hasAllPasswordFields) {
      const ok = validatePasswordFields();
      if (!ok) {
        return;
      }
    }

    const currentFirstName = profile.firstName?.trim() || '';
    const currentLastName = profile.lastName?.trim() || '';
    const currentEmail = profile.email?.trim() || '';
    
    const originalFirstName = originalProfile.firstName?.trim() || '';
    const originalLastName = originalProfile.lastName?.trim() || '';
    const originalEmail = originalProfile.email?.trim() || '';
    
    const firstNameChanged = currentFirstName !== originalFirstName;
    const lastNameChanged = currentLastName !== originalLastName;
    const emailChanged = currentEmail !== originalEmail;
    
    const hasProfileChanges = firstNameChanged || lastNameChanged || emailChanged;
    
    if (!hasProfileChanges && !hasAllPasswordFields) {
      toast.error('Please make at least one change before saving');
      return;
    }

    setSavingProfile(true);
    
    try {
      const profileUpdate = {};
      if (firstNameChanged) profileUpdate.first_name = currentFirstName;
      if (lastNameChanged) profileUpdate.last_name = currentLastName;
      if (emailChanged) profileUpdate.email = currentEmail;

      if (Object.keys(profileUpdate).length > 0) {
        await updateCurrentUserProfile(profileUpdate);
        toast.success('Profile updated successfully');
        
        setOriginalProfile({
          firstName: currentFirstName,
          lastName: currentLastName,
          email: currentEmail,
        });
      }

      if (hasAllPasswordFields) {
        const username = Cookies.get('username');
        if (!username) {
          toast.error('Username not found. Please log in again.');
          return;
        }

        try {
          await changePassword(
            currentPw,
            newPw,
            username
          );
          
          toast.success('Password changed successfully. Please log in again.');
          
          setProfile((prev) => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }));
          
          setTimeout(() => {
            Cookies.remove('authToken');
            Cookies.remove('username');
            Cookies.remove('userId');
            window.location.href = '/login';
          }, 2000);
          
        } catch (passwordError) {
          const errorMsg = passwordError.message || 'Failed to change password. Please check your current password and try again.';
          toast.error(errorMsg);
          return; 
        }
      } else {
        if (Object.keys(profileUpdate).length > 0) {
          await loadProfileData();
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      const errorMsg = error.message || 'Failed to save profile. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSavingProfile(false);
    }
  };

  const loadProfileData = async () => {
    setLoadingProfile(true);
    try {
      const profileData = await getCurrentUserProfile();
      const firstName = profileData.first_name || '';
      const lastName = profileData.last_name || '';
      const email = profileData.email || '';
      
      setProfile({
        username: profileData.username || '',
        firstName: firstName,
        lastName: lastName,
        email: email,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      setOriginalProfile({
        firstName: firstName,
        lastName: lastName,
        email: email,
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      const errorMsg = error.message || 'Failed to load profile. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    setProfileErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  }, []);

  useEffect(() => {
    const currentPw = profile.currentPassword?.trim() || '';
    const newPw = profile.newPassword?.trim() || '';
    const confirmPw = profile.confirmPassword?.trim() || '';
    
    const hasAnyPassword = currentPw || newPw || confirmPw;
    
    if (!hasAnyPassword) {
      setProfileErrors({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [profile.currentPassword, profile.newPassword, profile.confirmPassword]);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      setLoadingOptions(true);
      try {
        const [profileRes, settingsRes, timezonesRes, formatsRes] = await Promise.all([
          getCurrentUserProfile().catch(err => {
            console.error('Error loading profile:', err);
            return null; 
          }),
          getPublicTimezoneSettings(),
          getPublicTimezoneOptions(),
          getPublicDateFormatOptions(),
        ]);

        if (profileRes) {
          const firstName = profileRes.first_name || '';
          const lastName = profileRes.last_name || '';
          const email = profileRes.email || '';
          
          setProfile({
            username: profileRes.username || '',
            firstName: firstName,
            lastName: lastName,
            email: email,
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
          
          setOriginalProfile({
            firstName: firstName,
            lastName: lastName,
            email: email,
          });
        }

        if (Array.isArray(timezonesRes)) {
          setTimezoneOptions(timezonesRes);
        }

        if (Array.isArray(formatsRes)) {
          setDateFormatOptions(formatsRes);
        }

        if (settingsRes) {
          setTimezoneSettings({
            defaultTimezone: settingsRes.default_timezone || '',
            secondaryTimezone: settingsRes.secondary_timezone || '',
            dateFormat: settingsRes.date_format || '',
            alsoShowTimesInUtc: settingsRes.also_show_times_in_utc || false,
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error(
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load settings. Please try again.'
        );
      } finally {
        setLoading(false);
        setLoadingOptions(false);
      }
    };

    loadSettings();
  }, []);

  const handleSaveTimezone = async () => {
    if (!timezoneSettings.defaultTimezone) {
      toast.error('Please select a default timezone');
      return;
    }
    if (!timezoneSettings.dateFormat) {
      toast.error('Please select a date format');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        workflow_settings: {
          default_timezone: timezoneSettings.defaultTimezone,
          also_show_times_in_utc: timezoneSettings.alsoShowTimesInUtc,
          secondary_timezone: timezoneSettings.secondaryTimezone || null,
          date_format: timezoneSettings.dateFormat,
        },
      };

      await updatePublicTimezoneSettings(payload);
      toast.success('Timezone settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save settings. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };


  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#FAFBFD',
      }}
    >
      <Sidebar />

      {/* Main settings content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Page Header */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Settings
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 400 }}
          >
            Manage your profile, time preferences and notification controls for
            this web app.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Profile Settings */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #E5E7EB',
                bgcolor: '#FFFFFF',
              }}
            >
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                Profile Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mb: 2 }}
              >
                Update your basic account information and password.
              </Typography>

              <Grid container spacing={2}>
                {/* First / Last Name */}
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    label="First Name"
                    fullWidth
                    size="small"
                    value={profile.firstName}
                    onChange={handleProfileChange('firstName')}
                    disabled={loadingProfile || savingProfile}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    label="Last Name"
                    fullWidth
                    size="small"
                    value={profile.lastName}
                    onChange={handleProfileChange('lastName')}
                    disabled={loadingProfile || savingProfile}
                  />
                </Grid>

                {/* Username (read only) */}
                <Grid item xs={12}>
                  <StyledTextField
                    label="Username"
                    fullWidth
                    size="small"
                    value={profile.username}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled
                    helperText="Username cannot be changed."
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <StyledTextField
                    label="Email"
                    type="email"
                    fullWidth
                    size="small"
                    value={profile.email}
                    onChange={handleProfileChange('email')}
                    disabled={loadingProfile || savingProfile}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: 'text.secondary' }}
                  >
                    Change Password
                  </Typography>
                </Grid>

                {/* Current Password */}
                <Grid item xs={12}>
                  <StyledTextField
                    label="Current Password"
                    type={showPassword.current ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    value={profile.currentPassword}
                    onChange={handleProfileChange('currentPassword')}
                    error={!!profileErrors.currentPassword}
                    helperText={profileErrors.currentPassword || ' '}
                    autoComplete="off"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label="toggle current password visibility"
                            onClick={toggleShowPassword('current')}
                            size="small"
                          >
                            {showPassword.current ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* New Password */}
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    label="New Password"
                    type={showPassword.new ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    value={profile.newPassword}
                    onChange={handleProfileChange('newPassword')}
                    error={!!profileErrors.newPassword}
                    helperText={profileErrors.newPassword || ' '}
                    autoComplete="off"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label="toggle new password visibility"
                            onClick={toggleShowPassword('new')}
                            size="small"
                          >
                            {showPassword.new ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Confirm New Password */}
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    label="Confirm New Password"
                    type={showPassword.confirm ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    value={profile.confirmPassword}
                    onChange={handleProfileChange('confirmPassword')}
                    error={!!profileErrors.confirmPassword}
                    helperText={profileErrors.confirmPassword || ' '}
                    autoComplete="off"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label="toggle confirm password visibility"
                            onClick={toggleShowPassword('confirm')}
                            size="small"
                          >
                            {showPassword.confirm ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1.5}
                sx={{ mt: 3 }}
              >
                <Button
                  size="small"
                  variant="text"
                  sx={{ color: '#5965A6', textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ textTransform: 'none', bgcolor: '#5965A6' }}
                  onClick={handleSaveProfile}
                  disabled={savingProfile || loadingProfile}
                >
                  {savingProfile ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </Stack>
            </Paper>
          </Grid>

          {/* Timezone Settings */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #E5E7EB',
                bgcolor: '#FFFFFF',
              }}
            >
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                Timezone & Date Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mb: 2 }}
              >
                Configure how time and dates are displayed across the app.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <StyledTextField
                    select
                    label="Default Timezone"
                    fullWidth
                    size="small"
                    value={timezoneSettings.defaultTimezone}
                    onChange={handleTimezoneChange('defaultTimezone')}
                    disabled={loading || loadingOptions}
                  >
                    {loadingOptions ? (
                      <MenuItem disabled>
                        <CircularProgress size={18} sx={{ mr: 1 }} />
                        Loading timezones...
                      </MenuItem>
                    ) : timezoneOptions.length === 0 ? (
                      <MenuItem disabled>No timezones available</MenuItem>
                    ) : (
                      timezoneOptions.map((tz) => (
                        <MenuItem key={tz.timezone_id} value={tz.timezone_name}>
                          {tz.display_name || tz.timezone_name}
                        </MenuItem>
                      ))
                    )}
                  </StyledTextField>
                </Grid>

                <Grid item xs={12} sx={{ mb: 2 }}>
                  <StyledTextField
                    select
                    label="Secondary Timezone (optional)"
                    fullWidth
                    size="small"
                    value={timezoneSettings.secondaryTimezone}
                    onChange={handleTimezoneChange('secondaryTimezone')}
                    disabled={loading || loadingOptions}
                  >
                    <MenuItem value="">None</MenuItem>
                    {loadingOptions ? (
                      <MenuItem disabled>
                        <CircularProgress size={18} sx={{ mr: 1 }} />
                        Loading timezones...
                      </MenuItem>
                    ) : timezoneOptions.length === 0 ? (
                      <MenuItem disabled>No timezones available</MenuItem>
                    ) : (
                      timezoneOptions.map((tz) => (
                        <MenuItem key={tz.timezone_id} value={tz.timezone_name}>
                          {tz.display_name || tz.timezone_name}
                        </MenuItem>
                      ))
                    )}
                  </StyledTextField>
                </Grid>

                <Grid item xs={12} sx={{ mb: 2 }}>
                  <StyledTextField
                    select
                    label="Date Format"
                    fullWidth
                    size="small"
                    value={timezoneSettings.dateFormat}
                    onChange={handleTimezoneChange('dateFormat')}
                    disabled={loading || loadingOptions}
                  >
                    {loadingOptions ? (
                      <MenuItem disabled>
                        <CircularProgress size={18} sx={{ mr: 1 }} />
                        Loading date formats...
                      </MenuItem>
                    ) : dateFormatOptions.length === 0 ? (
                      <MenuItem disabled>No date formats available</MenuItem>
                    ) : (
                      dateFormatOptions.map((fmt) => (
                        <MenuItem key={fmt.format_id} value={fmt.format_code}>
                          {fmt.format_code}
                        </MenuItem>
                      ))
                    )}
                  </StyledTextField>
                </Grid>
              </Grid>

              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1.5}
                sx={{ mt: 3 }}
              >
                <Button
                  size="small"
                  variant="text"
                  sx={{ color: '#5965A6', textTransform: 'none' }}
                  disabled={loading || saving}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ textTransform: 'none', bgcolor: '#5965A6' }}
                  onClick={handleSaveTimezone}
                  disabled={loading || saving || loadingOptions}
                >
                  {saving ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Save Preferences'
                  )}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
